import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MakeApiCallAsync } from '../../../helpers/ApiHelpers';
import Config from '../../../helpers/Config';
import rootAction from '../../../stateManagment/actions/rootAction';
import { getLanguageCodeFromSession } from '../../../helpers/CommonHelper';
import { getPromotionProductUrl, getPromotionSearchTerm, matchPromotionProducts } from '../../../helpers/PromotionLinks';
import SiteBreadcrumb from '../../components/layout/SiteBreadcrumb';
import BestFacilities from '../../components/shared/BestFacilities';

export default function Campaign() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { campaign_id, langCode } = useParams();
    const language = langCode || getLanguageCodeFromSession();
    const [campaign, setCampaign] = useState(null);
    const [products, setProducts] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let active = true;
        setCampaign(null);
        setProducts([]);
        setError('');
        setLoading(true);
        const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
        const load = async () => {
            const response = await MakeApiCallAsync(Config.END_POINT_NAMES.GET_WEB_CAMPAIGN_DETAIL, null,
                { requestParameters: { CampaignId: campaign_id, recordValueJson: '[]' } }, headers, 'POST', true);
            const data = JSON.parse(response?.data?.data || '[]');
            const detail = data[0];
            if (!detail) throw new Error('This promotion is not available.');
            if (!active) return;
            setCampaign(detail);
            const direct = getPromotionProductUrl(detail, language);
            if (direct) { navigate(direct, { replace: true }); return; }
            const term = getPromotionSearchTerm(detail);
            if (!term) return;
            const productResponse = await MakeApiCallAsync(Config.END_POINT_NAMES.GET_All_PRODUCTS, null,
                { requestParameters: { SearchTerm: term, SizeID: null, ColorID: null, CategoryID: null,
                    TagID: null, ManufacturerID: null, MinPrice: null, MaxPrice: null, Rating: null,
                    OrderByColumnName: '', PageNo: 1, PageSize: 100, recordValueJson: '[]' } }, headers, 'POST', true);
            if (!productResponse?.data?.data) throw new Error('Unable to load the promoted product. Please try again.');
            const results = JSON.parse(productResponse.data.data);
            const matches = matchPromotionProducts(detail, Array.isArray(results) ? results : []);
            if (!active) return;
            // Only redirect for an unambiguous match from a complete result set.
            const total = Number(results?.[0]?.TotalRecords || results?.length || 0);
            if (matches.length === 1 && total <= results.length) navigate(getPromotionProductUrl(matches[0], language), { replace: true });
            else setProducts(matches);
        };
        dispatch(rootAction.commonAction.setLoading(true));
        load().catch(error => { if (active) setError(error.message); }).finally(() => {
            if (active) { setLoading(false); dispatch(rootAction.commonAction.setLoading(false)); }
        });
        return () => { active = false; dispatch(rootAction.commonAction.setLoading(false)); };
    }, [campaign_id, language, dispatch, navigate]);
    const path = String(campaign?.CoverPictureUrl || '').replace(/\\/g, '/');
    const image = /^https?:\/\//i.test(path) ? path : `${Config.ADMIN_BASE_URL.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
    return <>
        <Helmet><title>{Config.SITE_TTILE} - {campaign?.MainTitle || 'Promotion'}</title></Helmet>
        <SiteBreadcrumb title="Promotion" />
        <section className="about-area ptb-60"><div className="container">
            {loading && <p role="status">Opening the promoted product...</p>}
            {error && <p role="alert">{error}</p>}
            {campaign && <div className="row align-items-center">
                <div className="col-lg-6 col-md-12"><div className="about-content">
                    <h2>{campaign.MainTitle}</h2><h3>{campaign.DiscountTitle}</h3><p>{campaign.Body}</p>
                    {products.length > 0 && <><h4>Promoted products</h4><ul>{products.map(product => <li key={product.ProductId ?? product.ProductID}>
                        <Link to={getPromotionProductUrl(product, language)}>{product.ProductName} — View product details</Link>
                    </li>)}</ul></>}
                    {!loading && !products.length && <p>No matching product was found for this promotion.</p>}
                    <Link className="btn btn-primary" to={`/${language}/all-products/0/shop?SearchTerm=${encodeURIComponent(getPromotionSearchTerm(campaign))}`}>Browse products</Link>
                </div></div>
                {path && <div className="col-lg-6 col-md-12"><div className="about-image"><img src={image} alt={campaign.MainTitle || 'Promotion'} /></div></div>}
            </div>}
        </div></section>
        <BestFacilities />
    </>;
}
